import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faChevronCircleLeft, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import {
  PageContainer,
  SectionTitle,
  Button,
  Divider,
  fadeIn,
  Footer,
  useGoBack,
  FormRow, FormGroup, Label, Input,
  Table,
  TableHeader,
  TableRow,
  TableCell,
  TableFormGroup,
  SubSectionTitle,
  ButtonSuccess,
  Select
} from '../styles/PageStyles';
import { createModel, getModelByCode, getModelsConfig, updateModel } from '../services/modelHubService';
import { useToast } from '../components/common/ToastProvider';


const ModelHubDetails = () => {
  const { modelId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  

  const [modelDetail, setModelDetail] = useState(null);
  const [modelParams, setModelParams] = useState([]);
  const [modelConfig, setModelConfig] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isEditMode, setIsEditMode] = useState(false);
  const [tempParamValues, setTempParamValues] = useState({});
  
  const { showToast } = useToast();
  const goBack = useGoBack();


  const providersList = Object.keys(modelConfig ?? {});
  const modelNames = Object.keys(modelConfig?.[modelDetail?.provider] ?? {});


  useEffect(() => {
    const fetchModelConfig = async () => {
      try {
        const resp = await getModelsConfig();
        if (resp) {
          setModelConfig(resp);
        }
      } catch (error) {
        console.error('Error fetching model config', error);
        showToast({
          message: 'Error loading model configuration',
          type: 'error'
        });
      }
    };

    fetchModelConfig();
  }, []);


  useEffect(() => {
    const fetchModelByCode = async () => {
      try {
        setIsLoading(true);
        const response = await getModelByCode(modelId);
        
        if (response) {
          console.log('API Response:', response);
          setIsEditMode(true);
          
          // Map API response to our expected format
          const mappedModel = {
            id: response.id,
            code: response.code,
            company_cd: response.company_cd,
            name: response.name,
            type: response.model_type || response.type || '',
            status: response.status || 'ACTIVE',
            latency: response.latency || '',
            version: response.version || '',
            provider: normalizeProvider(response.provider), 
            performance: response.performance || 0,
            costPerToken: response.costPerToken || '$0.00010',
            displayName: response.displayName || '',
            description: response.description || '',
            icon: response.icon || '',
            config_params: response.config_params || {}
          };
          
          setModelDetail(mappedModel);
          

          const configParams = response.config_params || {};
          setTempParamValues(configParams);
        }
      } catch (error) {
        console.error('Error fetching model:', error);
        showToast({
          message: 'Error loading model',
          type: 'error'
        });
      } finally {
        setIsLoading(false);
      }
    };


    const initializeNewModel = () => {
      if (user) {
        setModelDetail({
          code: 'MDL-' + new Date().getTime(),
          company_cd: user.companyCd,
          name: '',
          type: '',
          status: 'ACTIVE',
          latency: '',
          version: '',
          provider: '',
          performance: 0,
          costPerToken: '$0.00010',
          displayName: '',
          description: '',
          icon: '',
          config_params: {}
        });
        setIsEditMode(false);
      }
      setIsLoading(false);
    };

    if (modelId !== 'new') {
      fetchModelByCode();
    } else {
      initializeNewModel();
    }
  }, [modelId, user]);

  const normalizeProvider = (provider) => {
    if (!provider) return '';
    const providerMap = {
      'azure_openai': 'Azure Openai',
      'azure openai': 'Azure Openai',
      'openai': 'OpenAI',
      'anthropic': 'Anthropic',
      'google': 'Google',
      'ollama': 'Ollama'
    };
    return providerMap[provider.toLowerCase()] || provider;
  };

  // Update model params when provider or model name changes
  useEffect(() => {
    if (modelDetail?.provider && modelDetail?.name && modelConfig) {
      const params = modelConfig?.[modelDetail.provider]?.[modelDetail.name];
      
      if (params && Array.isArray(params)) {
        // Map config params to include default values and user values
        const enrichedParams = params.map(param => ({
          ...param,
          userValue: tempParamValues?.[param.key] !== undefined 
            ? tempParamValues[param.key] 
            : param.default_value
        }));
        
        setModelParams(enrichedParams);
      } else {
        setModelParams([]);
      }
    } else {
      setModelParams([]);
    }
  }, [modelDetail?.provider, modelDetail?.name, modelConfig, tempParamValues]);

  // Handle model detail changes
  const handleModelDetails = (key, value) => {
    setModelDetail(prev => ({
      ...prev,
      [key]: value
    }));

    // Reset model name when provider changes
    if (key === 'provider') {
      setModelDetail(prev => ({
        ...prev,
        provider: value,
        name: ''
      }));
      setModelParams([]);
      setTempParamValues({});
    }
  };

  // Handle parameter value changes
  const handleParamChange = (paramKey, newValue) => {
    setTempParamValues(prev => ({
      ...prev,
      [paramKey]: newValue
    }));
  };

  // Add manual parameter
  const addConfigParam = () => {
    const newParam = {
      name: 'custom_param',
      key: `custom_${Date.now()}`,
      type: 'STRING',
      label: 'Custom Parameter',
      description: '',
      default_value: '',
      userValue: ''
    };
    
    setModelParams(prev => [...prev, newParam]);
  };

  // Remove parameter
  const removeConfigParam = (paramKey) => {
    setModelParams(prev => prev.filter(param => param.key !== paramKey));
    
    const newTempValues = { ...tempParamValues };
    delete newTempValues[paramKey];
    setTempParamValues(newTempValues);
  };

  // Build final config object for saving
  const buildConfigObject = () => {
    const configObj = {};
    
    modelParams.forEach(param => {
      const value = tempParamValues[param.key];
      if (value !== undefined && value !== '') {
        configObj[param.key] = value;
      }
    });
    
    return configObj;
  };

  // Save model
  const saveModel = async () => {
    try {
      // Validation
      if (!modelDetail?.provider) {
        showToast({
          message: 'Please select a provider',
          type: 'error'
        });
        return;
      }

      if (!modelDetail?.name) {
        showToast({
          message: 'Please select a model name',
          type: 'error'
        });
        return;
      }

      if (!modelDetail?.type) {
        showToast({
          message: 'Please select a model type',
          type: 'error'
        });
        return;
      }

      const configParams = buildConfigObject();
      
      // Prepare data for API - match your API structure
      const modelData = {
        id: modelDetail.id,
        code: modelDetail.code,
        company_cd: modelDetail.company_cd,
        name: modelDetail.name,
        model_type: modelDetail.type,
        status: modelDetail.status,
        latency: modelDetail.latency || '',
        version: modelDetail.version || '',
        provider: denormalizeProvider(modelDetail.provider), // Convert back to API format
        performance: modelDetail.performance || 0,
        costPerToken: modelDetail.costPerToken || '$0.00010',
        displayName: modelDetail.displayName || '',
        description: modelDetail.description || '',
        icon: modelDetail.icon || '',
        config_params: configParams
      };

      if (isEditMode && modelDetail?.id) {
        await updateModel(modelData);
        showToast({
          message: 'Model updated successfully',
          type: 'success'
        });
      } else {
        await createModel(modelData);
        showToast({
          message: 'Model created successfully',
          type: 'success'
        });
      }

      navigate('/model-hub');
    } catch (error) {
      console.error('Error saving model:', error);
      showToast({
        message: error.message || 'Error saving model',
        type: 'error'
      });
    }
  };


  const denormalizeProvider = (provider) => {
    if (!provider) return '';
    const providerMap = {
      'Azure Openai': 'azure_openai',
      'OpenAI': 'openai',
      'Anthropic': 'anthropic',
      'Google': 'google',
      'Ollama': 'ollama'
    };
    return providerMap[provider] || provider.toLowerCase().replace(/\s+/g, '_');
  };

  // Filter params based on search
  const filteredParams = modelParams.filter(param =>
    param.label?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    param.key?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get type badge class
  const getTypeBadgeClass = (type) => {
    switch (type) {
      case 'INT':
        return 'type-badge-int';
      case 'FLOAT':
        return 'type-badge-float';
      case 'BOOLEAN':
        return 'type-badge-boolean';
      default:
        return 'type-badge-string';
    }
  };

  if (isLoading) {
    return (
      <PageContainer>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
          <p>Loading...</p>
        </div>
      </PageContainer>
    );
  }

  if (!modelDetail) {
    return (
      <PageContainer>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
          <p>Error loading model</p>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <motion.div
        initial="hidden"
        animate="visible"
        variants={fadeIn}
      >
        <SectionTitle>
          <Button onClick={goBack} secondary>
            <FontAwesomeIcon icon={faChevronCircleLeft} />
          </Button>
          <span style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
            {isEditMode ? `Edit: ${modelDetail?.name}` : 'Add New Model'}
          </span>
        </SectionTitle>
        <Divider />

        {/* Model Details Section */}
        <FormRow>
          <FormGroup>
            <Label>Provider *</Label>
            <Select
              value={modelDetail?.provider || ''}
              onChange={(e) => handleModelDetails('provider', e.target.value)}
              disabled={isEditMode}
            >
              <option value="">Select Provider</option>
              {providersList?.length > 0 && providersList.map((provider) => (
                <option key={provider} value={provider}>
                  {provider}
                </option>
              ))}
            </Select>
          </FormGroup>

          <FormGroup>
            <Label>Model Name *</Label>
            <Select
              value={modelDetail?.name || ''}
              onChange={(e) => handleModelDetails('name', e.target.value)}
              disabled={!modelDetail?.provider}
            >
              <option value="">
                {modelDetail?.provider ? 'Select Model' : 'Select Provider First'}
              </option>
              {modelNames?.length > 0 && modelNames.map((model) => (
                <option key={model} value={model}>
                  {model}
                </option>
              ))}
            </Select>
          </FormGroup>

          <FormGroup>
            <Label>Type *</Label>
            <Select
              value={modelDetail?.type || ''}
              onChange={(e) => handleModelDetails('type', e.target.value)}
            >
              <option value="">Select Type</option>
              <option value="Chat">Chat</option>
              <option value="Reasoning">Reasoning</option>
              <option value="Embedding">Embedding</option>
              <option value="Vision">Vision</option>
            </Select>
          </FormGroup>

          <FormGroup>
            <Label>Status</Label>
            <Select
              value={modelDetail?.status || 'ACTIVE'}
              onChange={(e) => handleModelDetails('status', e.target.value)}
            >
              <option value="ACTIVE">Active</option>
              <option value="INACTIVE">Inactive</option>
            </Select>
          </FormGroup>

          <FormGroup>
            <Label>Version</Label>
            <Input
              type="text"
              value={modelDetail?.version || ''}
              onChange={(e) => handleModelDetails('version', e.target.value)}
              placeholder="e.g., 1.0.0"
            />
          </FormGroup>
        </FormRow>


        {/* Model Parameters Section */}
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginTop: '2rem', marginBottom: '1rem' }}>
          <SubSectionTitle>Model Parameters</SubSectionTitle>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <Input
              type="text"
              placeholder="Search parameters..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: '200px' }}
            />
            <ButtonSuccess onClick={addConfigParam}>
              <FontAwesomeIcon icon={faPlus} /> Add Param
            </ButtonSuccess>
          </div>
        </div>

        {/* Parameters Table */}
        {modelParams.length > 0 ? (
          <Table>
            <thead>
              <tr>
                <TableHeader>Parameter</TableHeader>
                <TableHeader>Type</TableHeader>
                <TableHeader>Default</TableHeader>
                <TableHeader>Description</TableHeader>
                <TableHeader>Actions</TableHeader>
              </tr>
            </thead>
            <tbody>
              {filteredParams.map((param, index) => (
                <TableRow key={param.key || index}>
                  <TableCell>
                    <TableFormGroup>
                      <Input
                        type="text"
                        disabled={!param.key.includes('custom')}
                        value={param?.label || ''}
                        placeholder="Parameter name"
                        onChange={(e) => {
                          // Allow editing label for custom params
                          if (param.key.includes('custom')) {
                            const updatedParams = modelParams.map(p =>
                              p.key === param.key ? { ...p, label: e.target.value } : p
                            );
                            setModelParams(updatedParams);
                          }
                        }}
                      />
                    </TableFormGroup>
                  </TableCell>

                  <TableCell>
                    <span style={{
                      display: 'inline-block',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: '500',
                      backgroundColor: 'var(--color-background-secondary)',
                      color: 'var(--color-text-primary)'
                    }}>
                      {param.type}
                    </span>
                  </TableCell>

                  <TableCell>
                    <span style={{ color: 'var(--color-text-secondary)', fontSize: '13px' }}>
                           <Input
                        type="text"
                        value={param.default_value ?? ''}
                        placeholder="Enter value"
                        onChange={(e) =>  (param.default_value, e.target.value)}
                      />
                    </span>
                  </TableCell>

                  <TableCell>
                    <span style={{ color: 'var(--color-text-secondary)', fontSize: '13px' }}>
                      {param.description || '-'}
                    </span>
                  </TableCell>


                  <TableCell>
                    <Button
                      onClick={() => removeConfigParam(param.key)}
                      style={{
                        padding: '0.3rem 0.6rem',
                        fontSize: '0.8rem',
                        color: 'white',
                        backgroundColor: '#ff4444',
                        borderColor: '#ff4444'
                      }}
                      title="Remove parameter"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </tbody>
          </Table>
        ) : (
          <div style={{
            textAlign: 'center',
            padding: '2rem',
            backgroundColor: 'var(--color-background-secondary)',
            borderRadius: '4px',
            color: 'var(--color-text-secondary)'
          }}>
            <p>
              {modelDetail?.provider && modelDetail?.name
                ? 'No parameters configured. Click "Add Param" to add custom parameters.'
                : 'Select a provider and model to load parameters.'}
            </p>
          </div>
        )}

        {/* Footer */}
        <Footer>
          <div style={{ flex: '1' }}></div>
          <Button onClick={goBack} style={{ marginRight: '0.5rem' }}>
            Cancel
          </Button>
          <Button primary onClick={saveModel}>
            <FontAwesomeIcon icon={faSave} />
            {isEditMode ? ' Update Model' : ' Create Model'}
          </Button>
        </Footer>
      </motion.div>
    </PageContainer>
  );
};

export default ModelHubDetails;